import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { examAPI } from '../../services/api';
import { getErrorMessage } from '../../utils/errorHandler';

export const fetchExams = createAsyncThunk('exams/fetchAll', async (params, { rejectWithValue }) => {
  try {
    const response = await examAPI.fetchExams(params);
    return response.data;
  } catch (error) {
    return rejectWithValue(getErrorMessage(error, 'Failed to fetch exams'));
  }
});

export const createExam = createAsyncThunk('exams/create', async (data, { rejectWithValue }) => {
  try {
    const response = await examAPI.createExam(data);
    return response.data.data;
  } catch (error) {
    return rejectWithValue(getErrorMessage(error, 'Failed to create exam'));
  }
});

export const updateExam = createAsyncThunk('exams/update', async ({ id, data }, { rejectWithValue }) => {
  try {
    const response = await examAPI.updateExam(id, data);
    return response.data.data;
  } catch (error) {
    return rejectWithValue(getErrorMessage(error, 'Failed to update exam'));
  }
});

export const deleteExam = createAsyncThunk('exams/delete', async (id, { rejectWithValue }) => {
  try {
    await examAPI.deleteExam(id);
    return id;
  } catch (error) {
    return rejectWithValue(getErrorMessage(error, 'Failed to delete exam'));
  }
});

export const enterResult = createAsyncThunk('exams/enterResult', async ({ id, data }, { rejectWithValue }) => {
  try {
    const response = await examAPI.enterResult(id, data);
    return response.data.data;
  } catch (error) {
    return rejectWithValue(getErrorMessage(error, 'Failed to enter result'));
  }
});

export const enterBulkResults = createAsyncThunk('exams/enterBulk', async ({ id, data }, { rejectWithValue }) => {
  try {
    const response = await examAPI.enterBulkResults(id, data);
    return response.data.data;
  } catch (error) {
    return rejectWithValue(getErrorMessage(error, 'Failed to enter bulk results'));
  }
});

export const fetchResults = createAsyncThunk('exams/fetchResults', async ({ id, params }, { rejectWithValue }) => {
  try {
    const response = await examAPI.fetchResults(id, params);
    return { examId: id, results: response.data.data || response.data };
  } catch (error) {
    return rejectWithValue(getErrorMessage(error, 'Failed to fetch results'));
  }
});

const initialState = {
  exams: [],
  currentExam: null,
  examResults: [],
  schedules: [],
  loading: false,
  error: null,
};

const examSlice = createSlice({
  name: 'exams',
  initialState,
  reducers: {
    clearExamError: (state) => { state.error = null; },
    setCurrentExam: (state, action) => { state.currentExam = action.payload; },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchExams.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchExams.fulfilled, (state, action) => { state.loading = false; state.exams = action.payload.data || action.payload; })
      .addCase(fetchExams.rejected, (state, action) => { state.loading = false; state.error = action.payload; })
      .addCase(createExam.fulfilled, (state, action) => { state.exams.unshift(action.payload); })
      .addCase(updateExam.fulfilled, (state, action) => { state.exams = state.exams.map((e) => e.id === action.payload.id ? action.payload : e); state.currentExam = action.payload; })
      .addCase(deleteExam.fulfilled, (state, action) => { state.exams = state.exams.filter((e) => e.id !== action.payload); })
      .addCase(enterResult.fulfilled, (state, action) => {
        state.examResults.push(action.payload);
        state.exams = state.exams.map((e) => e.id === action.payload.exam_id ? { ...e, results_count: (e.results_count || 0) + 1 } : e);
      })
      .addCase(enterBulkResults.fulfilled, (state, action) => {
        const results = Array.isArray(action.payload) ? action.payload : [action.payload];
        state.examResults = [...state.examResults, ...results];
      })
      .addCase(fetchResults.fulfilled, (state, action) => {
        state.examResults = action.payload.results;
      });
  },
});

export const { clearExamError, setCurrentExam } = examSlice.actions;
export default examSlice.reducer;
